import { FC, useCallback, useEffect, useMemo, useRef, useState } from "react"
import { FlexBox, HintTooltip, useToasts } from "@brg/ui"
import styled, { useTheme } from "styled-components"
import { useTranslation } from "react-i18next"
import { useProfile } from "@brg/auth"
import { LayoutConst, useModal } from "core"
import { SortingTypeIndex } from "shared/constants/sortingType"
import { ModalContainer, ProjectTabItem } from "shared/modals/sendProjects/styles"
import { SendProjectModalWrapper } from "shared/modals/sendProjects/employStyles"
import { SendProjectsEntity } from "shared/modals/sendProjects/SendProjectsEntity"
import { ProjectsButchController } from "core/controllers/ProjectsButchController"
import { RingPositions } from "shared/constants/ringPositionType"
import { BrgButton, ButtonVariant } from "@brg/ui"
import { CreateLinkTo } from "shared/constants/linkTo"
import type { PortfolioFolder } from "shared/pages/magistracy/hooks/useMagistracyPortfolioFolders"
import { useMagistracyPortfolioFolders } from "shared/pages/magistracy/hooks/useMagistracyPortfolioFolders"
import { BTagsGridNew } from "core/components/bTag/bTagsGridNew/bTagsGridNew"
import { BTag } from "core/components"
import { useDispatch, useSelector } from "react-redux"
import ProfileFoldersSlice, { ProfileFoldersProps, ProfileFoldersSelector } from "shared/reducers/ProfileFoldersSlice"
import { FolderEdit } from "shared/pages/designer/components/DesignerFolders/components/FolderEdit"
import { FolderOptions } from "shared/pages/designer/components/DesignerFolders/components/folderOptions"
import { FolderEmptyOptions } from "shared/pages/designer/components/DesignerFolders/components/folderEmptyOptions"
import { useFoldersController } from "shared/hooks/useFoldersController"
import { FOLDERS_MODE, FolderT } from "shared/pages/designer/components/tabs/const"
import PlusSvg from "shared/assets/32/PlusSvg"
import { getTooltipStyles } from "shared/constants/tooptipStyles"

const FoldersPanel = styled.div`
  width: 100%;
  background: #f2f2f2;
  border-radius: 3px;
  padding: 10px 12px;
`

const FolderEditPanel = styled.div`
  width: 100%;
  margin-top: 8px;
`

const EmptyStateTitle = styled.h3`
  margin: 0;
  font-size: 30px;
  line-height: 40px;
  letter-spacing: -0.02em;
  color: #151514;

  @media (max-width: ${LayoutConst.screen.s}px) {
    font-size: 20px;
    line-height: 25px;
  }
`

const EmptyStateDescription = styled.p`
  margin: 0;
  font-size: 17px;
  line-height: 22px;
  color: #70706f;
  text-align: center;

  @media (max-width: ${LayoutConst.screen.s}px) {
    font-size: 13px;
    line-height: 16px;
  }
`

type MagistracyPortfolioModalProps = {
  initialSelectedProjectIds?: string[]
  initialFolderId?: string
  folders?: PortfolioFolder[]
  onFoldersChange?: () => void | Promise<void>
  onAttach: (selectedProjectIds: string[], folderId?: string) => Promise<boolean> | boolean
}

export const MagistracyPortfolioModal: FC<MagistracyPortfolioModalProps> = ({
  initialSelectedProjectIds = [],
  initialFolderId,
  folders = [],
  onFoldersChange,
  onAttach,
}) => {
  const { t } = useTranslation()
  const theme = useTheme()
  const modal = useModal()
  const dispatch = useDispatch()
  const profile = useProfile()
  const { info } = useToasts()
  const { onEdit, onDelete } = useFoldersController()
  const { fetchFolders } = useMagistracyPortfolioFolders({
    designerId: profile?.designerId,
  })
  const [localFolders, setLocalFolders] = useState<PortfolioFolder[]>(folders)
  const { currentFolder } = useSelector(ProfileFoldersSelector)
  const previousFolderRef = useRef<ProfileFoldersProps>(currentFolder)
  const viewerId = profile?.accountId || profile?.designerId
  const [selectedFolderId, setSelectedFolderId] = useState<string | undefined>(initialFolderId)
  const [viewTab, setViewTab] = useState<"all" | "selected">("all")
  const [sorting, setSorting] = useState<SortingTypeIndex>(SortingTypeIndex.moderation)
  const [date, setDate] = useState<number>(Date.now())
  const [selectedProjectIds, setSelectedProjectIds] = useState<string[]>(initialSelectedProjectIds)
  const [loadedProjectsCount, setLoadedProjectsCount] = useState<number>(0)
  const [hasLoadedProjects, setHasLoadedProjects] = useState<boolean>(false)
  const [totalProjectsCount, setTotalProjectsCount] = useState<number>(0)

  useEffect(() => {
    let cancelled = false

    fetchFolders().then((data) => {
      if (cancelled) {
        return
      }

      setLocalFolders((current) => {
        if (current.length === 0) {
          return data
        }

        return data.map((fetched) => {
          const local = current.find((item) => item.id === fetched.id)
          if (!local) {
            return fetched
          }

          const localCount = local.entityIds?.length ?? 0
          const fetchedCount = fetched.entityIds?.length ?? 0
          return fetchedCount >= localCount ? fetched : local
        })
      })
    })

    return () => {
      cancelled = true
    }
  }, [fetchFolders])

  const refreshFolders = useCallback(async () => {
    await onFoldersChange?.()
    const refreshedFolders = await fetchFolders()
    setLocalFolders(refreshedFolders)
    return refreshedFolders
  }, [fetchFolders, onFoldersChange])

  const normalizedFolderId = useMemo(() => {
    if (!selectedFolderId) {
      return undefined
    }
    return localFolders.some((folder) => folder.id === selectedFolderId) ? selectedFolderId : undefined
  }, [selectedFolderId, localFolders])
  const activeFolderId = normalizedFolderId || "all"

  useEffect(() => {
    dispatch(ProfileFoldersSlice.actions.setCurrentFolder(ProfileFoldersProps.projects))

    return () => {
      dispatch(ProfileFoldersSlice.actions.setCurrentFolder(previousFolderRef.current))
    }
  }, [dispatch])

  const handleFolderSelect = useCallback((folderId?: string) => {
    setSelectedFolderId((currentFolderId) => {
      if (folderId && currentFolderId === folderId) {
        return undefined
      }
      return folderId
    })
    dispatch(ProfileFoldersSlice.actions.setCurrentFolder(ProfileFoldersProps.projects))
  }, [dispatch])

  const activeFolder = useMemo(
    () => localFolders.find((folder) => folder.id === activeFolderId),
    [activeFolderId, localFolders]
  )

  const selectedFolderProjectIds = useMemo(() => {
    if (!activeFolder) {
      return undefined
    }
    return Array.isArray(activeFolder.entityIds) ? activeFolder.entityIds : []
  }, [activeFolder])

  const folderContentKey = useMemo(
    () => (selectedFolderProjectIds ?? []).join(","),
    [selectedFolderProjectIds]
  )

  useEffect(() => {
    setHasLoadedProjects(false)
    setLoadedProjectsCount(0)
    setTotalProjectsCount(0)
  }, [activeFolderId, folderContentKey])

  const projectsFilter = useMemo(() => {
    if (!viewerId) {
      return undefined
    }

    if (activeFolderId !== "all") {
      if (!selectedFolderProjectIds?.length) {
        return undefined
      }

      return {
        projectIds: selectedFolderProjectIds,
        projectFilter: sorting,
        isContextIgnored: true,
        projectStatus: [1],
        onlyAuthors: false,
        limit: 8,
        OrderByProjectIds: true,
      }
    }

    return {
      authorIds: [viewerId],
      projectFilter: sorting,
      isContextIgnored: true,
      projectStatus: [1],
      onlyAuthors: false,
      limit: 8,
    }
  }, [activeFolderId, selectedFolderProjectIds, sorting, viewerId])

  const handleSelectProject = useCallback((projectId: string) => {
    setSelectedProjectIds((prev) => {
      if (prev.includes(projectId)) {
        return prev.filter((id) => id !== projectId)
      }
      return [...prev, projectId]
    })
  }, [])

  const isAllProjectsView = activeFolderId === "all"

  const effectiveCount = useMemo(() => {
    if (isAllProjectsView) return selectedProjectIds.length
    return selectedFolderProjectIds?.length ?? 0
  }, [isAllProjectsView, selectedProjectIds, selectedFolderProjectIds])

  const activeTabLabel = useMemo(() => {
    if (isAllProjectsView) return t("magistracyPage.portfolio.modal.all_projects")
    const name = activeFolder?.name ?? ""
    return name.length > 20 ? name.slice(0, 20) + "…" : name
  }, [isAllProjectsView, activeFolder, t])

  const isFolderFormVisible = useMemo(
    () => [ProfileFoldersProps.create, ProfileFoldersProps.edit].includes(currentFolder),
    [currentFolder]
  )

  const isEmptyFolder = useMemo(() => {
    if (!activeFolder) return false
    return !activeFolder.entityIds || activeFolder.entityIds.length === 0
  }, [activeFolder])

  const handleAttach = useCallback(async () => {
    const isSuccess = isAllProjectsView
      ? await onAttach(selectedProjectIds, undefined)
      : await onAttach([], activeFolderId)

    if (isSuccess) {
      if (isAllProjectsView) {
        setSelectedProjectIds([])
      }
      modal.hide()
    }
  }, [modal, onAttach, selectedProjectIds, activeFolderId, isAllProjectsView])

  const handleOpenCreateFolder = useCallback(() => {
    dispatch(ProfileFoldersSlice.actions.setCurrentFolder(ProfileFoldersProps.create))
    setSelectedFolderId(undefined)
  }, [dispatch])

  const handleFolderEditCallback = useCallback(async () => {
    await refreshFolders()
    dispatch(ProfileFoldersSlice.actions.setCurrentFolder(ProfileFoldersProps.projects))
  }, [dispatch, refreshFolders])

  const handleEditToggle = useCallback(() => {
    if (currentFolder === ProfileFoldersProps.edit) {
      dispatch(ProfileFoldersSlice.actions.setCurrentFolder(ProfileFoldersProps.projects))
      return
    }
    dispatch(ProfileFoldersSlice.actions.setCurrentFolder(ProfileFoldersProps.edit))
  }, [currentFolder, dispatch])

  const toFolderT = useCallback(
    (folder: PortfolioFolder): FolderT => ({
      ...folder,
      description: folder.description ?? "",
      accountId: profile?.accountId || profile?.designerId || "",
    }),
    [profile?.accountId, profile?.designerId]
  )

  const handleChooseFolderProjects = useCallback(
    (folder: FolderT) => {
      const newIds = folder.entityIds ?? []
      const updatedFolders = localFolders.map((fl) => {
        if (fl.id === folder.id) {
          return { ...fl, entityIds: newIds }
        }
        return {
          ...fl,
          entityIds: fl.entityIds?.filter((id) => !newIds.includes(id)) ?? [],
        }
      })

      Promise.all(
        updatedFolders.map((item) => onEdit(toFolderT(item), FOLDERS_MODE.edit, true))
      ).then(async () => {
        await onFoldersChange?.()

        let mergedFolders: PortfolioFolder[] = updatedFolders
        try {
          const refreshedFolders = await fetchFolders()
          mergedFolders = refreshedFolders.map((refreshed) => {
            if (refreshed.id !== folder.id) {
              return refreshed
            }
            const refreshedIds = refreshed.entityIds ?? []
            if (refreshedIds.length >= newIds.length) {
              return refreshed
            }
            return { ...refreshed, entityIds: newIds }
          })
        } catch (error) {
          console.error("Failed to refresh folders after adding projects", error)
        }

        modal.updatePrev({ folders: mergedFolders })
        modal.back()
        info(t("folders.modal.success_edit", { name: folder.name }))
      })
    },
    [fetchFolders, info, localFolders, modal, onEdit, onFoldersChange, t, toFolderT]
  )

  const handleAddProjectsToFolder = useCallback(() => {
    if (!activeFolder) return

    modal.show("folder-projects-edit", {
      folder: toFolderT(activeFolder),
      onSubmit: handleChooseFolderProjects,
      onCustomClose: () => modal.back(),
    })
  }, [activeFolder, handleChooseFolderProjects, modal, toFolderT])

  const handleDeleteFolder = useCallback(() => {
    if (!activeFolder?.id) return

    onDelete(activeFolder.id, true)?.then(async () => {
      info(t("folders.modal.success_delete_folder", { name: activeFolder.name }))
      await refreshFolders()
      setSelectedFolderId(undefined)
    })
  }, [activeFolder, info, onDelete, refreshFolders, t])

  const handlePinToggle = useCallback(() => {
    if (!activeFolder) return

    const folderData = toFolderT(activeFolder)
    const otherDefaultList = localFolders.filter((folder) => folder.id !== activeFolder.id && folder.isDefault)

    if (otherDefaultList.length > 0) {
      Promise.all(
        otherDefaultList.map((folder) =>
          onEdit({ ...toFolderT(folder), isDefault: false }, FOLDERS_MODE.edit, true)
        )
      )
    }

    onEdit(
      {
        ...folderData,
        isDefault: !folderData.isDefault,
        ...(!folderData.isDefault && { isHidden: false }),
      },
      FOLDERS_MODE.edit,
      true
    ).then(() => {
      handleFolderEditCallback()
    })
  }, [activeFolder, localFolders, onEdit, handleFolderEditCallback, toFolderT])

  const handleHiddenToggle = useCallback(() => {
    if (!activeFolder) return

    onEdit(
      {
        ...toFolderT(activeFolder),
        isHidden: !activeFolder.isHidden,
      },
      FOLDERS_MODE.edit,
      true
    ).then(() => {
      handleFolderEditCallback()
    })
  }, [activeFolder, onEdit, handleFolderEditCallback, toFolderT])

  const handleSortingChange = useCallback((value: SortingTypeIndex) => {
    setSorting(value)
    setDate(Date.now())
  }, [])

  const showEmptyFolderState = useMemo(() => {
    return (
      viewTab === "all" &&
      !isFolderFormVisible &&
      activeFolderId !== "all" &&
      Array.isArray(selectedFolderProjectIds) &&
      selectedFolderProjectIds.length === 0
    )
  }, [viewTab, isFolderFormVisible, activeFolderId, selectedFolderProjectIds])

  const showGlobalEmptyState = useMemo(() => {
    return (
      viewTab === "all" &&
      !isFolderFormVisible &&
      activeFolderId === "all" &&
      hasLoadedProjects &&
      loadedProjectsCount === 0
    )
  }, [viewTab, isFolderFormVisible, activeFolderId, hasLoadedProjects, loadedProjectsCount])

  const selectedProjectsFilter = useMemo(() => {
    if (!viewerId || selectedProjectIds.length === 0) return undefined
    return {
      projectIds: selectedProjectIds,
      projectFilter: sorting,
      isContextIgnored: true,
      projectStatus: [1],
      onlyAuthors: false,
      limit: 8,
    }
  }, [viewerId, selectedProjectIds, sorting])

  const folderProjectsFilter = useMemo(() => {
    if (!viewerId || isAllProjectsView || !selectedFolderProjectIds?.length) return undefined
    return {
      projectIds: selectedFolderProjectIds,
      projectFilter: sorting,
      isContextIgnored: true,
      projectStatus: [1],
      onlyAuthors: false,
      limit: 8,
    }
  }, [viewerId, isAllProjectsView, selectedFolderProjectIds, sorting])

  return (
    <ModalContainer style={{ overflow: "auto", height: "100%" }} justify="flex-start" offsetTop={88} offsetTopMobile={1}>
      <SendProjectModalWrapper direction="column" justify="flex-start" align="center" style={{ position: "relative" }}>
        <SendProjectsEntity
          titleText={t("magistracyPage.portfolio.modal.title")}
          subtitleText={t("magistracyPage.portfolio.modal.subtitle")}
          hideFilter={viewTab === "selected" || showEmptyFolderState}
          hideCount
          count={effectiveCount}
          sendText={t("magistracyPage.portfolio.modal.attach", { count: effectiveCount })}
          isDisabledSendButton={effectiveCount === 0}
          onMobileSortingClick={() => undefined}
          onSortingChange={handleSortingChange}
          sorting={sorting}
          onSend={handleAttach}
          tabs={
            <FlexBox gx={24}>
              <ProjectTabItem isActive={viewTab === "all"} onClick={() => setViewTab("all")}>
                {activeTabLabel}
                {totalProjectsCount > 0 ? ` | ${totalProjectsCount}` : ""}
              </ProjectTabItem>
              <ProjectTabItem isActive={viewTab === "selected"} onClick={() => setViewTab("selected")}>
                {t("magistracyPage.portfolio.modal.tab_selected")} | {effectiveCount}
              </ProjectTabItem>
            </FlexBox>
          }
        >
          {viewTab === "all" && (
            <FoldersPanel>
              <BTagsGridNew style={{ gap: 8 }}>
                <BTag
                  isShort
                  isTagsCloud
                  isFolder
                  selected={activeFolderId === "all"}
                  onClick={() => handleFolderSelect(undefined)}
                >
                  {t("magistracyPage.portfolio.modal.all_projects")}
                </BTag>
                {localFolders.map((folder) => (
                  <BTag
                    key={folder.id}
                    isShort
                    isTagsCloud
                    isFolder
                    selected={selectedFolderId === folder.id}
                    onClick={() => handleFolderSelect(folder.id)}
                  >
                    {folder.name}
                  </BTag>
                ))}
                <HintTooltip
                  // @ts-ignore
                  customStyle={getTooltipStyles(theme)}
                  text={t("hints.create_folder")}
                  z={1}
                  noDisappearDelay
                  positionY="default"
                  onWhiteBg
                >
                  <BTag
                    isShort
                    isTagsCloud
                    isFolder
                    isCreateAction
                    onClick={handleOpenCreateFolder}
                  >
                    <PlusSvg />
                  </BTag>
                </HintTooltip>
              </BTagsGridNew>
            </FoldersPanel>
          )}

          {viewTab === "all" && isFolderFormVisible && (
            <FolderEditPanel>
              <FolderEdit
                activeFolder={
                  currentFolder === ProfileFoldersProps.edit && activeFolder
                    ? toFolderT(activeFolder)
                    : undefined
                }
                folderId={currentFolder === ProfileFoldersProps.edit ? normalizedFolderId : undefined}
                callback={handleFolderEditCallback}
              />
            </FolderEditPanel>
          )}

          {viewTab === "all" && !isFolderFormVisible && activeFolder && !isAllProjectsView && (
            <FolderOptions
              activeFolder={toFolderT(activeFolder)}
              currentFolder={currentFolder}
              isEmptyFolder={isEmptyFolder}
              onEdit={handleEditToggle}
              onPin={handlePinToggle}
              onHidden={handleHiddenToggle}
              onAdd={handleAddProjectsToFolder}
            />
          )}

          {viewTab === "all" && !isFolderFormVisible && showGlobalEmptyState && (
            <FlexBox direction="column" gy={16} align="center" offsetTop={48}>
              <EmptyStateTitle>{t("magistracyPage.portfolio.modal.empty_title")}</EmptyStateTitle>
              <EmptyStateDescription>{t("magistracyPage.portfolio.modal.empty_description")}</EmptyStateDescription>
              <FlexBox width={228} widthMobile="100%">
                <BrgButton
                  variant={ButtonVariant.Filled}
                  fullWidth
                  onClick={() => window.open(CreateLinkTo.projectCreate(), "_SELF")}
                >
                  {t("magistracyPage.portfolio.modal.create_project")}
                </BrgButton>
              </FlexBox>
            </FlexBox>
          )}

          {viewTab === "all" && !isFolderFormVisible && showEmptyFolderState && activeFolder && (
            <FolderEmptyOptions
              isMe
              activeFolder={toFolderT(activeFolder)}
              onAdd={handleAddProjectsToFolder}
              onDelete={handleDeleteFolder}
            />
          )}

          {viewTab === "all" && !isFolderFormVisible && !showGlobalEmptyState && !showEmptyFolderState && projectsFilter && (
            <ProjectsButchController
              key={`${activeFolderId}-${folderContentKey}`}
              filter={projectsFilter}
              selectable={isAllProjectsView}
              unselectable={isAllProjectsView}
              modal={false}
              metaAlwaysVisible
              withoutGoToProject
              withoutQueryParams
              disableFilterCheck
              selectRingPosition={RingPositions.right}
              selectedProjects={isAllProjectsView ? selectedProjectIds : []}
              onSelect={isAllProjectsView ? handleSelectProject : undefined}
              date={date}
              setProjectCount={(count) => {
                if (count != null) setTotalProjectsCount(count)
              }}
              onProjectsLoaded={(projects) => {
                if (projects != null) {
                  setHasLoadedProjects(true)
                  setLoadedProjectsCount(projects.length ?? 0)
                }
              }}
            />
          )}

          {viewTab === "selected" && isAllProjectsView && selectedProjectsFilter && (
            <ProjectsButchController
              key="selected-view"
              filter={selectedProjectsFilter}
              selectable
              unselectable
              modal={false}
              metaAlwaysVisible
              withoutGoToProject
              withoutQueryParams
              disableFilterCheck
              selectRingPosition={RingPositions.right}
              selectedProjects={selectedProjectIds}
              onSelect={handleSelectProject}
              date={date}
            />
          )}

          {viewTab === "selected" && !isAllProjectsView && folderProjectsFilter && (
            <ProjectsButchController
              key={`selected-folder-${activeFolderId}-${folderContentKey}`}
              filter={folderProjectsFilter}
              modal={false}
              metaAlwaysVisible
              withoutGoToProject
              withoutQueryParams
              disableFilterCheck
              date={date}
            />
          )}

          {viewTab === "selected" && effectiveCount === 0 && (
            <FlexBox direction="column" gy={12} align="center" offsetTop={48}>
              <EmptyStateTitle>{t("magistracyPage.portfolio.modal.no_selected_title")}</EmptyStateTitle>
            </FlexBox>
          )}
        </SendProjectsEntity>
      </SendProjectModalWrapper>
    </ModalContainer>
  )
}
import { FC, useCallback, useEffect, useMemo, useRef, useState } from "react"
import { FlexBox, HintTooltip, useToasts } from "@brg/ui"
import styled, { useTheme } from "styled-components"
import { useTranslation } from "react-i18next"
import { useProfile } from "@brg/auth"
import { LayoutConst, useModal } from "core"
import { SortingTypeIndex } from "shared/constants/sortingType"
import { ModalContainer, ProjectTabItem } from "shared/modals/sendProjects/styles"
import { SendProjectModalWrapper } from "shared/modals/sendProjects/employStyles"
import { SendProjectsEntity } from "shared/modals/sendProjects/SendProjectsEntity"
import { ProjectsButchController } from "core/controllers/ProjectsButchController"
import { RingPositions } from "shared/constants/ringPositionType"
import { BrgButton, ButtonVariant } from "@brg/ui"
import { CreateLinkTo } from "shared/constants/linkTo"
import type { PortfolioFolder } from "shared/pages/magistracy/hooks/useMagistracyPortfolioFolders"
import { useMagistracyPortfolioFolders } from "shared/pages/magistracy/hooks/useMagistracyPortfolioFolders"
import { BTagsGridNew } from "core/components/bTag/bTagsGridNew/bTagsGridNew"
import { BTag } from "core/components"
import { useDispatch, useSelector } from "react-redux"
import ProfileFoldersSlice, { ProfileFoldersProps, ProfileFoldersSelector } from "shared/reducers/ProfileFoldersSlice"
import { FolderEdit } from "shared/pages/designer/components/DesignerFolders/components/FolderEdit"
import { FolderOptions } from "shared/pages/designer/components/DesignerFolders/components/folderOptions"
import { FolderEmptyOptions } from "shared/pages/designer/components/DesignerFolders/components/folderEmptyOptions"
import { useFoldersController } from "shared/hooks/useFoldersController"
import { FOLDERS_MODE, FolderT } from "shared/pages/designer/components/tabs/const"
import PlusSvg from "shared/assets/32/PlusSvg"
import { getTooltipStyles } from "shared/constants/tooptipStyles"

const FoldersPanel = styled.div`
  width: 100%;
  background: #f2f2f2;
  border-radius: 3px;
  padding: 10px 12px;
`

const FolderEditPanel = styled.div`
  width: 100%;
  margin-top: 8px;
`

const EmptyStateTitle = styled.h3`
  margin: 0;
  font-size: 30px;
  line-height: 40px;
  letter-spacing: -0.02em;
  color: #151514;

  @media (max-width: ${LayoutConst.screen.s}px) {
    font-size: 20px;
    line-height: 25px;
  }
`

const EmptyStateDescription = styled.p`
  margin: 0;
  font-size: 17px;
  line-height: 22px;
  color: #70706f;
  text-align: center;

  @media (max-width: ${LayoutConst.screen.s}px) {
    font-size: 13px;
    line-height: 16px;
  }
`

type MagistracyPortfolioModalProps = {
  initialSelectedProjectIds?: string[]
  initialFolderId?: string
  folders?: PortfolioFolder[]
  onFoldersChange?: () => void | Promise<void>
  onAttach: (selectedProjectIds: string[], folderId?: string) => Promise<boolean> | boolean
}

export const MagistracyPortfolioModal: FC<MagistracyPortfolioModalProps> = ({
  initialSelectedProjectIds = [],
  initialFolderId,
  folders = [],
  onFoldersChange,
  onAttach,
}) => {
  const { t } = useTranslation()
  const theme = useTheme()
  const modal = useModal()
  const dispatch = useDispatch()
  const profile = useProfile()
  const { info } = useToasts()
  const { onEdit, onDelete } = useFoldersController()
  const { fetchFolders } = useMagistracyPortfolioFolders({
    designerId: profile?.designerId,
  })
  const [localFolders, setLocalFolders] = useState<PortfolioFolder[]>(folders)
  const { currentFolder } = useSelector(ProfileFoldersSelector)
  const previousFolderRef = useRef<ProfileFoldersProps>(currentFolder)
  const viewerId = profile?.accountId || profile?.designerId
  const [selectedFolderId, setSelectedFolderId] = useState<string | undefined>(initialFolderId)
  const [viewTab, setViewTab] = useState<"all" | "selected">("all")
  const [sorting, setSorting] = useState<SortingTypeIndex>(SortingTypeIndex.moderation)
  const [date, setDate] = useState<number>(Date.now())
  const [selectedProjectIds, setSelectedProjectIds] = useState<string[]>(initialSelectedProjectIds)
  const [loadedProjectsCount, setLoadedProjectsCount] = useState<number>(0)
  const [hasLoadedProjects, setHasLoadedProjects] = useState<boolean>(false)
  const [totalProjectsCount, setTotalProjectsCount] = useState<number>(0)

  useEffect(() => {
    let cancelled = false

    fetchFolders().then((data) => {
      if (cancelled) {
        return
      }

      setLocalFolders((current) => {
        if (current.length === 0) {
          return data
        }

        return data.map((fetched) => {
          const local = current.find((item) => item.id === fetched.id)
          if (!local) {
            return fetched
          }

          const localCount = local.entityIds?.length ?? 0
          const fetchedCount = fetched.entityIds?.length ?? 0
          return fetchedCount >= localCount ? fetched : local
        })
      })
    })

    return () => {
      cancelled = true
    }
  }, [fetchFolders])

  const refreshFolders = useCallback(async () => {
    await onFoldersChange?.()
    const refreshedFolders = await fetchFolders()
    setLocalFolders(refreshedFolders)
    return refreshedFolders
  }, [fetchFolders, onFoldersChange])

  const normalizedFolderId = useMemo(() => {
    if (!selectedFolderId) {
      return undefined
    }
    return localFolders.some((folder) => folder.id === selectedFolderId) ? selectedFolderId : undefined
  }, [selectedFolderId, localFolders])
  const activeFolderId = normalizedFolderId || "all"

  useEffect(() => {
    dispatch(ProfileFoldersSlice.actions.setCurrentFolder(ProfileFoldersProps.projects))

    return () => {
      dispatch(ProfileFoldersSlice.actions.setCurrentFolder(previousFolderRef.current))
    }
  }, [dispatch])

  const handleFolderSelect = useCallback((folderId?: string) => {
    setSelectedFolderId((currentFolderId) => {
      if (folderId && currentFolderId === folderId) {
        return undefined
      }
      return folderId
    })
    dispatch(ProfileFoldersSlice.actions.setCurrentFolder(ProfileFoldersProps.projects))
  }, [dispatch])

  const activeFolder = useMemo(
    () => localFolders.find((folder) => folder.id === activeFolderId),
    [activeFolderId, localFolders]
  )

  const selectedFolderProjectIds = useMemo(() => {
    if (!activeFolder) {
      return undefined
    }
    return Array.isArray(activeFolder.entityIds) ? activeFolder.entityIds : []
  }, [activeFolder])

  const folderContentKey = useMemo(
    () => (selectedFolderProjectIds ?? []).join(","),
    [selectedFolderProjectIds]
  )

  useEffect(() => {
    setHasLoadedProjects(false)
    setLoadedProjectsCount(0)
    setTotalProjectsCount(0)
  }, [activeFolderId, folderContentKey])

  const projectsFilter = useMemo(() => {
    if (!viewerId) {
      return undefined
    }

    if (activeFolderId !== "all") {
      if (!selectedFolderProjectIds?.length) {
        return undefined
      }

      return {
        projectIds: selectedFolderProjectIds,
        projectFilter: sorting,
        isContextIgnored: true,
        projectStatus: [1],
        onlyAuthors: false,
        limit: 8,
        OrderByProjectIds: true,
      }
    }

    return {
      authorIds: [viewerId],
      projectFilter: sorting,
      isContextIgnored: true,
      projectStatus: [1],
      onlyAuthors: false,
      limit: 8,
    }
  }, [activeFolderId, selectedFolderProjectIds, sorting, viewerId])

  const handleSelectProject = useCallback((projectId: string) => {
    setSelectedProjectIds((prev) => {
      if (prev.includes(projectId)) {
        return prev.filter((id) => id !== projectId)
      }
      return [...prev, projectId]
    })
  }, [])

  const isAllProjectsView = activeFolderId === "all"

  const effectiveCount = useMemo(() => {
    if (isAllProjectsView) return selectedProjectIds.length
    return selectedFolderProjectIds?.length ?? 0
  }, [isAllProjectsView, selectedProjectIds, selectedFolderProjectIds])

  const activeTabLabel = useMemo(() => {
    if (isAllProjectsView) return t("magistracyPage.portfolio.modal.all_projects")
    const name = activeFolder?.name ?? ""
    return name.length > 20 ? name.slice(0, 20) + "…" : name
  }, [isAllProjectsView, activeFolder, t])

  const isFolderFormVisible = useMemo(
    () => [ProfileFoldersProps.create, ProfileFoldersProps.edit].includes(currentFolder),
    [currentFolder]
  )

  const isEmptyFolder = useMemo(() => {
    if (!activeFolder) return false
    return !activeFolder.entityIds || activeFolder.entityIds.length === 0
  }, [activeFolder])

  const handleAttach = useCallback(async () => {
    const isSuccess = isAllProjectsView
      ? await onAttach(selectedProjectIds, undefined)
      : await onAttach([], activeFolderId)

    if (isSuccess) {
      if (isAllProjectsView) {
        setSelectedProjectIds([])
      }
      modal.hide()
    }
  }, [modal, onAttach, selectedProjectIds, activeFolderId, isAllProjectsView])

  const handleOpenCreateFolder = useCallback(() => {
    dispatch(ProfileFoldersSlice.actions.setCurrentFolder(ProfileFoldersProps.create))
    setSelectedFolderId(undefined)
  }, [dispatch])

  const handleFolderEditCallback = useCallback(async () => {
    await refreshFolders()
    dispatch(ProfileFoldersSlice.actions.setCurrentFolder(ProfileFoldersProps.projects))
  }, [dispatch, refreshFolders])

  const handleEditToggle = useCallback(() => {
    if (currentFolder === ProfileFoldersProps.edit) {
      dispatch(ProfileFoldersSlice.actions.setCurrentFolder(ProfileFoldersProps.projects))
      return
    }
    dispatch(ProfileFoldersSlice.actions.setCurrentFolder(ProfileFoldersProps.edit))
  }, [currentFolder, dispatch])

  const toFolderT = useCallback(
    (folder: PortfolioFolder): FolderT => ({
      ...folder,
      description: folder.description ?? "",
      accountId: profile?.accountId || profile?.designerId || "",
    }),
    [profile?.accountId, profile?.designerId]
  )

  const handleChooseFolderProjects = useCallback(
    (folder: FolderT) => {
      const newIds = folder.entityIds ?? []
      const updatedFolders = localFolders.map((fl) => {
        if (fl.id === folder.id) {
          return { ...fl, entityIds: newIds }
        }
        return {
          ...fl,
          entityIds: fl.entityIds?.filter((id) => !newIds.includes(id)) ?? [],
        }
      })

      Promise.all(
        updatedFolders.map((item) => onEdit(toFolderT(item), FOLDERS_MODE.edit, true))
      ).then(async () => {
        await onFoldersChange?.()

        let mergedFolders: PortfolioFolder[] = updatedFolders
        try {
          const refreshedFolders = await fetchFolders()
          mergedFolders = refreshedFolders.map((refreshed) => {
            if (refreshed.id !== folder.id) {
              return refreshed
            }
            const refreshedIds = refreshed.entityIds ?? []
            if (refreshedIds.length >= newIds.length) {
              return refreshed
            }
            return { ...refreshed, entityIds: newIds }
          })
        } catch (error) {
          console.error("Failed to refresh folders after adding projects", error)
        }

        modal.updatePrev({ folders: mergedFolders })
        modal.back()
        info(t("folders.modal.success_edit", { name: folder.name }))
      })
    },
    [fetchFolders, info, localFolders, modal, onEdit, onFoldersChange, t, toFolderT]
  )

  const handleAddProjectsToFolder = useCallback(() => {
    if (!activeFolder) return

    modal.show("folder-projects-edit", {
      folder: toFolderT(activeFolder),
      onSubmit: handleChooseFolderProjects,
      onCustomClose: () => modal.back(),
    })
  }, [activeFolder, handleChooseFolderProjects, modal, toFolderT])

  const handleDeleteFolder = useCallback(() => {
    if (!activeFolder?.id) return

    onDelete(activeFolder.id, true)?.then(async () => {
      info(t("folders.modal.success_delete_folder", { name: activeFolder.name }))
      await refreshFolders()
      setSelectedFolderId(undefined)
    })
  }, [activeFolder, info, onDelete, refreshFolders, t])

  const handlePinToggle = useCallback(() => {
    if (!activeFolder) return

    const folderData = toFolderT(activeFolder)
    const otherDefaultList = localFolders.filter((folder) => folder.id !== activeFolder.id && folder.isDefault)

    if (otherDefaultList.length > 0) {
      Promise.all(
        otherDefaultList.map((folder) =>
          onEdit({ ...toFolderT(folder), isDefault: false }, FOLDERS_MODE.edit, true)
        )
      )
    }

    onEdit(
      {
        ...folderData,
        isDefault: !folderData.isDefault,
        ...(!folderData.isDefault && { isHidden: false }),
      },
      FOLDERS_MODE.edit,
      true
    ).then(() => {
      handleFolderEditCallback()
    })
  }, [activeFolder, localFolders, onEdit, handleFolderEditCallback, toFolderT])

  const handleHiddenToggle = useCallback(() => {
    if (!activeFolder) return

    onEdit(
      {
        ...toFolderT(activeFolder),
        isHidden: !activeFolder.isHidden,
      },
      FOLDERS_MODE.edit,
      true
    ).then(() => {
      handleFolderEditCallback()
    })
  }, [activeFolder, onEdit, handleFolderEditCallback, toFolderT])

  const handleSortingChange = useCallback((value: SortingTypeIndex) => {
    setSorting(value)
    setDate(Date.now())
  }, [])

  const showEmptyFolderState = useMemo(() => {
    return (
      viewTab === "all" &&
      !isFolderFormVisible &&
      activeFolderId !== "all" &&
      Array.isArray(selectedFolderProjectIds) &&
      selectedFolderProjectIds.length === 0
    )
  }, [viewTab, isFolderFormVisible, activeFolderId, selectedFolderProjectIds])

  const showGlobalEmptyState = useMemo(() => {
    return (
      viewTab === "all" &&
      !isFolderFormVisible &&
      activeFolderId === "all" &&
      hasLoadedProjects &&
      loadedProjectsCount === 0
    )
  }, [viewTab, isFolderFormVisible, activeFolderId, hasLoadedProjects, loadedProjectsCount])

  const selectedProjectsFilter = useMemo(() => {
    if (!viewerId || selectedProjectIds.length === 0) return undefined
    return {
      projectIds: selectedProjectIds,
      projectFilter: sorting,
      isContextIgnored: true,
      projectStatus: [1],
      onlyAuthors: false,
      limit: 8,
    }
  }, [viewerId, selectedProjectIds, sorting])

  const folderProjectsFilter = useMemo(() => {
    if (!viewerId || isAllProjectsView || !selectedFolderProjectIds?.length) return undefined
    return {
      projectIds: selectedFolderProjectIds,
      projectFilter: sorting,
      isContextIgnored: true,
      projectStatus: [1],
      onlyAuthors: false,
      limit: 8,
    }
  }, [viewerId, isAllProjectsView, selectedFolderProjectIds, sorting])

  return (
    <ModalContainer style={{ overflow: "auto", height: "100%" }} justify="flex-start" offsetTop={88} offsetTopMobile={1}>
      <SendProjectModalWrapper direction="column" justify="flex-start" align="center" style={{ position: "relative" }}>
        <SendProjectsEntity
          titleText={t("magistracyPage.portfolio.modal.title")}
          subtitleText={t("magistracyPage.portfolio.modal.subtitle")}
          hideFilter={viewTab === "selected" || showEmptyFolderState}
          hideCount
          count={effectiveCount}
          sendText={t("magistracyPage.portfolio.modal.attach", { count: effectiveCount })}
          isDisabledSendButton={effectiveCount === 0}
          onMobileSortingClick={() => undefined}
          onSortingChange={handleSortingChange}
          sorting={sorting}
          onSend={handleAttach}
          tabs={
            <FlexBox gx={24}>
              <ProjectTabItem isActive={viewTab === "all"} onClick={() => setViewTab("all")}>
                {activeTabLabel}
                {totalProjectsCount > 0 ? ` | ${totalProjectsCount}` : ""}
              </ProjectTabItem>
              <ProjectTabItem isActive={viewTab === "selected"} onClick={() => setViewTab("selected")}>
                {t("magistracyPage.portfolio.modal.tab_selected")} | {effectiveCount}
              </ProjectTabItem>
            </FlexBox>
          }
        >
          {viewTab === "all" && (
            <FoldersPanel>
              <BTagsGridNew style={{ gap: 8 }}>
                <BTag
                  isShort
                  isTagsCloud
                  isFolder
                  selected={activeFolderId === "all"}
                  onClick={() => handleFolderSelect(undefined)}
                >
                  {t("magistracyPage.portfolio.modal.all_projects")}
                </BTag>
                {localFolders.map((folder) => (
                  <BTag
                    key={folder.id}
                    isShort
                    isTagsCloud
                    isFolder
                    selected={selectedFolderId === folder.id}
                    onClick={() => handleFolderSelect(folder.id)}
                  >
                    {folder.name}
                  </BTag>
                ))}
                <HintTooltip
                  // @ts-ignore
                  customStyle={getTooltipStyles(theme)}
                  text={t("hints.create_folder")}
                  z={1}
                  noDisappearDelay
                  positionY="default"
                  onWhiteBg
                >
                  <BTag
                    isShort
                    isTagsCloud
                    isFolder
                    isCreateAction
                    onClick={handleOpenCreateFolder}
                  >
                    <PlusSvg />
                  </BTag>
                </HintTooltip>
              </BTagsGridNew>
            </FoldersPanel>
          )}

          {viewTab === "all" && isFolderFormVisible && (
            <FolderEditPanel>
              <FolderEdit
                activeFolder={
                  currentFolder === ProfileFoldersProps.edit && activeFolder
                    ? toFolderT(activeFolder)
                    : undefined
                }
                folderId={currentFolder === ProfileFoldersProps.edit ? normalizedFolderId : undefined}
                callback={handleFolderEditCallback}
              />
            </FolderEditPanel>
          )}

          {viewTab === "all" && !isFolderFormVisible && activeFolder && !isAllProjectsView && (
            <FolderOptions
              activeFolder={toFolderT(activeFolder)}
              currentFolder={currentFolder}
              isEmptyFolder={isEmptyFolder}
              onEdit={handleEditToggle}
              onPin={handlePinToggle}
              onHidden={handleHiddenToggle}
              onAdd={handleAddProjectsToFolder}
            />
          )}

          {viewTab === "all" && !isFolderFormVisible && showGlobalEmptyState && (
            <FlexBox direction="column" gy={16} align="center" offsetTop={48}>
              <EmptyStateTitle>{t("magistracyPage.portfolio.modal.empty_title")}</EmptyStateTitle>
              <EmptyStateDescription>{t("magistracyPage.portfolio.modal.empty_description")}</EmptyStateDescription>
              <FlexBox width={228} widthMobile="100%">
                <BrgButton
                  variant={ButtonVariant.Filled}
                  fullWidth
                  onClick={() => window.open(CreateLinkTo.projectCreate(), "_SELF")}
                >
                  {t("magistracyPage.portfolio.modal.create_project")}
                </BrgButton>
              </FlexBox>
            </FlexBox>
          )}

          {viewTab === "all" && !isFolderFormVisible && showEmptyFolderState && activeFolder && (
            <FolderEmptyOptions
              isMe
              activeFolder={toFolderT(activeFolder)}
              onAdd={handleAddProjectsToFolder}
              onDelete={handleDeleteFolder}
            />
          )}

          {viewTab === "all" && !isFolderFormVisible && !showGlobalEmptyState && !showEmptyFolderState && projectsFilter && (
            <ProjectsButchController
              key={`${activeFolderId}-${folderContentKey}`}
              filter={projectsFilter}
              selectable={isAllProjectsView}
              unselectable={isAllProjectsView}
              modal={false}
              metaAlwaysVisible
              withoutGoToProject
              withoutQueryParams
              disableFilterCheck
              selectRingPosition={RingPositions.right}
              selectedProjects={isAllProjectsView ? selectedProjectIds : []}
              onSelect={isAllProjectsView ? handleSelectProject : undefined}
              date={date}
              setProjectCount={(count) => {
                if (count != null) setTotalProjectsCount(count)
              }}
              onProjectsLoaded={(projects) => {
                if (projects != null) {
                  setHasLoadedProjects(true)
                  setLoadedProjectsCount(projects.length ?? 0)
                }
              }}
            />
          )}

          {viewTab === "selected" && isAllProjectsView && selectedProjectsFilter && (
            <ProjectsButchController
              key="selected-view"
              filter={selectedProjectsFilter}
              selectable
              unselectable
              modal={false}
              metaAlwaysVisible
              withoutGoToProject
              withoutQueryParams
              disableFilterCheck
              selectRingPosition={RingPositions.right}
              selectedProjects={selectedProjectIds}
              onSelect={handleSelectProject}
              date={date}
            />
          )}

          {viewTab === "selected" && !isAllProjectsView && folderProjectsFilter && (
            <ProjectsButchController
              key={`selected-folder-${activeFolderId}-${folderContentKey}`}
              filter={folderProjectsFilter}
              modal={false}
              metaAlwaysVisible
              withoutGoToProject
              withoutQueryParams
              disableFilterCheck
              date={date}
            />
          )}

          {viewTab === "selected" && effectiveCount === 0 && (
            <FlexBox direction="column" gy={12} align="center" offsetTop={48}>
              <EmptyStateTitle>{t("magistracyPage.portfolio.modal.no_selected_title")}</EmptyStateTitle>
            </FlexBox>
          )}
        </SendProjectsEntity>
      </SendProjectModalWrapper>
    </ModalContainer>
  )
}
